import React, { useState, useRef, useMemo, useCallback } from 'react';
import { Product, productService, ProductPrice, QUERY_KEYS } from '@/services/productService';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ImageIcon, AlertCircle, UploadCloud, MoreHorizontal } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/utils';
import { PricingModal } from './PricingModal';
import { CategoryModal } from './CategoryModal';
import { ProductTagsModal } from './ProductTagsModal';
import { FamilyChangeModal } from './FamilyChangeModal';
import { Breadcrumb, BreadcrumbItem, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Category } from '@/types/categories';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useProductAssets } from '@/hooks/useProductAssets';
import { invalidateProductAndAssets } from '@/utils/queryInvalidation';
import StatusToggle from '@/components/ui/StatusToggle';
import {
  formatNumberForDisplay,
  formatDateForDisplay,
  DateFormat,
  getRelativeTimeString,
} from '@/utils/numberFormat';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';
import { OptimizedImage, useOptimizedImage } from '@/components/ui/OptimizedImage';

import { getPrimaryAsset } from '@/utils/images';
import EnhancedInlineEditField from '@/components/ui/EnhancedInlineEditField';
import { isValidBarcodeFormat } from '@/schemas/product';
import { useAuth } from '@/contexts/AuthContext';
import { logError } from '@/utils/errorHandler';
import { ProductSyncButtons } from './ProductSyncButtons';

// Extend the Product interface to include prices
interface ExtendedProduct extends Product {
  id: number; // Ensure id is always present
  prices?: ProductPrice[];
  all_prices?: ProductPrice[];
  family?: {
    id: number;
    code: string;
    label: string;
    description?: string;
  };
}

interface ProductDetailSidebarProps {
  product: ExtendedProduct;
  prices: ProductPrice[];
  isPricesLoading: boolean;
}

function ProductDetailSidebar({ product, prices, isPricesLoading }: ProductDetailSidebarProps) {
  const queryClient = useQueryClient();
  const { checkPermission } = useAuth();
  const hasEditPermission = checkPermission('product.change');
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [showFamilyModal, setShowFamilyModal] = useState(false);
  // Get translations
  const { getSidebarLabels, getSidebarNotification } = useProductDetailPageTranslations();

  // Use the product assets hook for asset management
  const { assets } = useProductAssets(product.id);

  // Get organization settings for formatting
  const { orgSettings } = useOrgSettings();
  const numberFormat = orgSettings?.number_format || 'dot';
  const dateFormat = (orgSettings?.date_format || 'MDY') as DateFormat;

  // Reorder prices to prioritize organization's default price type
  const prioritizedPrices = useMemo(() => {
    if (!prices || prices.length === 0) return prices;

    const defaultPriceType = orgSettings?.default_price_type;
    if (!defaultPriceType) return prices; // No default price type, use original order

    // Find price matching default price type
    const defaultPriceIndex = prices.findIndex(
      price =>
        price.price_type === defaultPriceType.code ||
        price.price_type === defaultPriceType.id?.toString() ||
        price.price_type_display === defaultPriceType.label
    );

    if (defaultPriceIndex === -1) return prices; // No matching price found, use original order

    // Move default price to front
    const reorderedPrices = [...prices];
    const [defaultPrice] = reorderedPrices.splice(defaultPriceIndex, 1);
    return [defaultPrice, ...reorderedPrices];
  }, [prices, orgSettings?.default_price_type]);

  // Helper function to format currency with organization preferences
  const formatCurrencyWithOrgFormat = (
    amount: number | string | null | undefined,
    currency: string
  ) => {
    // Convert to number and handle edge cases
    let numericAmount: number;

    if (typeof amount === 'number') {
      numericAmount = amount;
    } else if (typeof amount === 'string') {
      numericAmount = parseFloat(amount);
    } else {
      numericAmount = 0;
    }

    // Handle invalid numbers
    if (isNaN(numericAmount)) {
      numericAmount = 0;
    }

    if (numberFormat === 'comma') {
      const formattedNumber = formatNumberForDisplay(numericAmount, 'comma', 2);
      return `${formattedNumber} ${currency}`;
    } else {
      return formatCurrency(numericAmount, currency);
    }
  };

  // Inline edit mutation hooks
  const makeFieldMutation = (field: keyof Product) =>
    useMutation({
      mutationFn: (value: string) => productService.updateProduct(product.id!, { [field]: value }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PRODUCT(product.id) });
        // Don't show toast here - InlineEditField handles it
      },
      onError: error => {
        // Don't show toast here - InlineEditField handles it
        throw error; // Re-throw so InlineEditField can handle it
      },
    });

  const skuMutation = makeFieldMutation('sku');
  const nameMutation = makeFieldMutation('name');
  const brandMutation = makeFieldMutation('brand');
  const gtinMutation = makeFieldMutation('barcode'); // GTIN maps to barcode field

  // Status mutation
  const statusMutation = useMutation({
    mutationFn: (isActive: boolean) =>
      productService.updateProduct(product.id!, { is_active: isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PRODUCT(product.id) });
    },
    onError: error => {
      throw error; // Re-throw so StatusToggle can handle it
    },
  });

  // Wrapper functions to match InlineEditField's expected signature
  const handleSkuSave = async (value: string): Promise<void> => {
    await skuMutation.mutateAsync(value);
  };

  const handleNameSave = async (value: string): Promise<void> => {
    await nameMutation.mutateAsync(value);
  };

  const handleBrandSave = async (value: string): Promise<void> => {
    await brandMutation.mutateAsync(value);
  };

  const handleGtinSave = async (value: string): Promise<void> => {
    await gtinMutation.mutateAsync(value);
  };

  // Status toggle handler
  const handleStatusToggle = async (isActive: boolean): Promise<void> => {
    await statusMutation.mutateAsync(isActive);
  };

  const createdDate = {
    display: product.created_at
      ? formatDateForDisplay(product.created_at, dateFormat)
      : getSidebarLabels.meta.unknown,
    relative: product.created_at
      ? getRelativeTimeString(product.created_at)
      : getSidebarLabels.meta.unknown,
  };
  const modifiedDate = {
    display: product.updated_at
      ? formatDateForDisplay(product.updated_at, dateFormat)
      : getSidebarLabels.meta.unknown,
    relative: product.updated_at
      ? getRelativeTimeString(product.updated_at)
      : getSidebarLabels.meta.unknown,
  };

  // Extract and narrow the category trail to eliminate TypeScript errors
  const categoryTrail: Category[] = Array.isArray(product.category) ? product.category : [];
  // Extract single category for the fallback case
  const singleCategory =
    !Array.isArray(product.category) &&
    typeof product.category === 'object' &&
    product.category !== null
      ? (product.category as Category)
      : null;

  // Get primary asset
  const primaryAsset = useMemo(() => {
    if (assets?.length) {
      return assets.find(asset => asset.is_primary) || assets[0];
    }
    return getPrimaryAsset(product);
  }, [product, assets]);

  // Use optimized image hook
  const { hasImage } = useOptimizedImage(primaryAsset);

  // Update handlePricesUpdated to return a Promise with selective invalidation
  const handlePricesUpdated = useCallback(async (): Promise<void> => {
    try {
      // Only invalidate prices, not all product data to prevent cascade
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PRODUCT_PRICES(product.id || 0),
      });
      toast.success(getSidebarNotification('pricesUpdated'));
    } catch (error) {
      logError(
        'Failed to refresh after price update',
        {
          component: 'ProductDetailSidebar',
          action: 'handlePricesUpdated',
          metadata: {
            productId: product.id,
            error,
          },
        },
        { showToast: false, userFacing: false }
      );
      toast.error(getSidebarNotification('refreshAfterPriceFailed'));
    }
  }, [queryClient, product.id, getSidebarNotification]);

  // Update the handleShowPricing function
  const handleShowPricing = () => {
    if (!product?.id) return;
    setShowPricingModal(true);
  };

  // Function to refresh product data
  const handleRefreshProduct = async () => {
    if (!product?.id) return;

    try {
      await invalidateProductAndAssets(queryClient, product.id);
      toast.success(getSidebarNotification('refreshSuccess'));
    } catch (error) {
      logError(
        'Failed to refresh product data',
        {
          component: 'ProductDetailSidebar',
          action: 'handleRefreshProduct',
          metadata: {
            productId: product.id,
            error,
          },
        },
        { showToast: false, userFacing: false }
      );
      toast.error(getSidebarNotification('refreshFailed'));
    }
  };

  // Handle family update callback
  const handleFamilyChanged = (_updatedProduct: Product) => {
    // Invalidate queries to refresh the product data
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PRODUCT(product.id) });
    queryClient.invalidateQueries({ queryKey: ['products'], exact: false });
  };

  // Handle image upload
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const shouldSetPrimary = !assets || assets.length === 0;
      await productService.uploadAsset(product.id, file, undefined, shouldSetPrimary);

      // Invalidate queries using the correct query keys that match QUERY_KEYS constants
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PRODUCT(product.id) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PRODUCT_ASSETS(product.id) });
      queryClient.invalidateQueries({ queryKey: ['products'], exact: false }); // Also refresh products list

      // Force refetch of active queries for immediate UI update
      await Promise.all([
        queryClient.refetchQueries({
          queryKey: QUERY_KEYS.PRODUCT(product.id),
          type: 'active',
        }),
        queryClient.refetchQueries({
          queryKey: QUERY_KEYS.PRODUCT_ASSETS(product.id),
          type: 'active',
        }),
      ]);

      toast.success('Image uploaded successfully');
    } catch (error) {
      logError(
        'Failed to upload product image',
        {
          component: 'ProductDetailSidebar',
          action: 'handleImageUpload',
          metadata: {
            productId: product.id,
            fileName: file.name,
            fileSize: file.size,
            error,
          },
        },
        { showToast: false, userFacing: false }
      );
      toast.error('Failed to upload image');
    } finally {
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        {/* Hero image section */}
        {hasImage ? (
          <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
            <DialogTrigger asChild>
              <div
                className="relative h-48 w-full cursor-pointer overflow-hidden bg-muted/30 dark:bg-muted/20"
                tabIndex={0}
                aria-label={getSidebarLabels.tooltips.viewProductImage}
                onKeyDown={e => e.key === 'Enter' && setImageDialogOpen(true)}
              >
                <OptimizedImage
                  asset={primaryAsset}
                  alt={product.name}
                  context="detail"
                  className="size-full transition-transform hover:scale-105"
                  clickable
                />
              </div>
            </DialogTrigger>
            <DialogContent className="size-auto max-h-[95vh] max-w-[95vw] p-6">
              <div className="flex size-full max-h-[85vh] max-w-[85vw] items-center justify-center">
                <img
                  src={primaryAsset?.url || primaryAsset?.file}
                  alt={product.name}
                  className="max-h-full max-w-full object-contain"
                  style={{ maxWidth: '100%', maxHeight: '100%' }}
                />
              </div>
            </DialogContent>
          </Dialog>
        ) : (
          <div className="flex h-48 w-full flex-col items-center justify-center bg-muted/30 dark:bg-muted/20">
            <ImageIcon className="mb-2 size-12 text-muted-foreground" />
            {hasEditPermission && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <UploadCloud className="mr-2 size-4" />
                  {getSidebarLabels.buttons.uploadImage}
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={handleImageUpload}
                />
              </>
            )}
          </div>
        )}

        {/* Core product fields */}
        <div className="space-y-3 p-4">
          <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
            {getSidebarLabels.sections.coreInformation}
          </h2>

          {/* SKU with inline edit */}
          <EnhancedInlineEditField
            label={getSidebarLabels.fields.sku}
            fieldName="sku"
            value={product.sku}
            validate={val => (val.trim() ? true : getSidebarLabels.validation.skuRequired)}
            onSave={handleSkuSave}
            disabled={!hasEditPermission}
            context="update"
            enhanced={true}
            realTimeValidation={true}
          />

          {/* Name with inline edit */}
          <EnhancedInlineEditField
            label={getSidebarLabels.fields.name}
            fieldName="name"
            value={product.name}
            validate={val => (val.trim() ? true : getSidebarLabels.validation.nameRequired)}
            onSave={handleNameSave}
            disabled={!hasEditPermission}
            context="update"
            enhanced={true}
          />

          {/* Category */}
          <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
            <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
              {getSidebarLabels.sections.category}
            </h2>
            {hasEditPermission && (
              <Button
                size="sm"
                variant="outline"
                className="h-6 text-xs"
                onClick={() => setShowCategoryModal(true)}
              >
                {getSidebarLabels.buttons.change}
              </Button>
            )}
          </div>
          <Breadcrumb>
            {categoryTrail.length > 0 ? (
              categoryTrail.map((cat, idx) => (
                <React.Fragment key={cat.id}>
                  <BreadcrumbItem isCurrent={idx === categoryTrail.length - 1}>
                    <span
                      className={
                        idx === categoryTrail.length - 1
                          ? 'font-medium text-foreground'
                          : 'cursor-pointer text-muted-foreground hover:text-foreground hover:underline'
                      }
                      onClick={() => {
                        if (idx < categoryTrail.length - 1) {
                          window.location.href = `/products?category=${cat.id}`;
                        }
                      }}
                    >
                      {cat.name}
                    </span>
                  </BreadcrumbItem>
                  {idx < categoryTrail.length - 1 && <BreadcrumbSeparator />}
                </React.Fragment>
              ))
            ) : singleCategory ? (
              <BreadcrumbItem isCurrent>
                <span>
                  {product.category_name ||
                    singleCategory.name ||
                    getSidebarLabels.states.uncategorized}
                </span>
              </BreadcrumbItem>
            ) : typeof product.category === 'string' ? (
              <BreadcrumbItem isCurrent>
                <span>{product.category.trim() || getSidebarLabels.states.uncategorized}</span>
              </BreadcrumbItem>
            ) : (
              <BreadcrumbItem isCurrent>
                <span className="text-muted-foreground">
                  {getSidebarLabels.states.uncategorized}
                </span>
              </BreadcrumbItem>
            )}
          </Breadcrumb>

          {/* Family */}
          <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
            <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
              {getSidebarLabels.sections.family}
            </h2>
            {hasEditPermission && (
              <Button
                size="sm"
                variant="outline"
                className="h-6 text-xs"
                onClick={() => setShowFamilyModal(true)}
              >
                {getSidebarLabels.buttons.change}
              </Button>
            )}
          </div>
          <div className="rounded-md border border-border bg-muted/30 px-3 py-2 dark:bg-muted/20">
            {product.family ? (
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground">{product.family.label}</span>
                <Badge className="text-xs">{product.family.code}</Badge>
                {product.family.description && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="text-muted-foreground transition-colors hover:text-foreground">
                          <AlertCircle className="size-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">{product.family.description}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">
                {getSidebarLabels.states.noFamilyAssigned}
              </span>
            )}
          </div>

          {/* Brand and GTIN with inline edit */}
          <div className="mt-3 space-y-3 border-t border-border pt-3">
            <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
              {getSidebarLabels.sections.additionalInformation}
            </h2>

            {/* Brand with inline edit */}
            <EnhancedInlineEditField
              label={getSidebarLabels.fields.brand}
              fieldName="brand"
              value={product.brand || ''}
              onSave={handleBrandSave}
              disabled={!hasEditPermission}
              placeholder={getSidebarLabels.placeholders.enterBrandName}
            />

            {/* GTIN with inline edit */}
            <EnhancedInlineEditField
              label={getSidebarLabels.fields.gtin}
              fieldName="barcode"
              value={product.barcode || ''}
              validate={val => {
                if (!val || val.trim() === '') return true;
                return isValidBarcodeFormat(val) ? true : getSidebarLabels.validation.gtinFormat;
              }}
              onSave={handleGtinSave}
              disabled={!hasEditPermission}
              placeholder={getSidebarLabels.placeholders.enterGtinBarcode}
            />
          </div>

          {/* Tags */}
          <div className="mt-3 border-t border-border pt-3">
            <div className="mb-1 flex items-center justify-between">
              <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
                {getSidebarLabels.sections.tags}
              </h2>
              {hasEditPermission && (
                <Button variant="ghost" size="sm" onClick={() => setShowTagsModal(true)}>
                  {getSidebarLabels.buttons.manage}
                </Button>
              )}
            </div>
            <div className="flex flex-wrap gap-1 rounded-md border border-border bg-muted/30 px-3 py-1.5 dark:bg-muted/20">
              {product.tags && product.tags.length > 0 ? (
                <>
                  {product.tags.slice(0, 5).map((tag, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className="border-primary/30 bg-primary/10 text-xs text-primary dark:bg-primary/20 dark:text-primary"
                    >
                      {tag}
                    </Badge>
                  ))}

                  {product.tags.length > 5 && (
                    <HoverCard>
                      <HoverCardTrigger asChild>
                        <Badge
                          variant="outline"
                          className="cursor-pointer border-border bg-card text-xs text-foreground hover:bg-accent"
                        >
                          {getSidebarLabels.states.moreItems.replace(
                            '{{count}}',
                            (product.tags.length - 5).toString()
                          )}
                        </Badge>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-auto p-2">
                        <div className="flex flex-wrap gap-1">
                          {product.tags.slice(5).map((tag, index) => (
                            <Badge
                              key={index}
                              variant="outline"
                              className="border-primary/30 bg-primary/10 text-xs text-primary dark:bg-primary/20 dark:text-primary"
                            >
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  )}
                </>
              ) : (
                <span className="text-sm text-muted-foreground">
                  {getSidebarLabels.states.noTags}
                </span>
              )}
            </div>
          </div>

          {/* PRICING section */}
          <div>
            <div className="flex items-center justify-between">
              <h3 className="mb-2 text-sm font-medium uppercase tracking-wider text-muted-foreground">
                {getSidebarLabels.sections.pricing}
              </h3>
              {hasEditPermission && (
                <Button variant="ghost" size="sm" onClick={handleShowPricing}>
                  {getSidebarLabels.buttons.manage}
                </Button>
              )}
            </div>

            {/* Display primary price */}
            <div className="mb-2 rounded border border-yellow-200 bg-yellow-50 p-2 dark:border-yellow-800/30 dark:bg-yellow-900/20">
              {isPricesLoading ? (
                <div className="text-md animate-pulse text-muted-foreground">
                  {getSidebarLabels.states.loadingPrices}
                </div>
              ) : prioritizedPrices && prioritizedPrices.length > 0 ? (
                <>
                  <div className="text-2xl font-bold text-foreground">
                    {formatCurrencyWithOrgFormat(
                      prioritizedPrices[0].amount,
                      prioritizedPrices[0].currency || 'USD'
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {prioritizedPrices[0].price_type_display || prioritizedPrices[0].price_type}
                  </div>
                </>
              ) : (
                <div className="text-xl italic text-muted-foreground">
                  {getSidebarLabels.states.noPriceSet}
                </div>
              )}
            </div>

            {/* Additional prices (up to 3) */}
            {prioritizedPrices && prioritizedPrices.length > 1 && (
              <div className="mt-2 space-y-2">
                {prioritizedPrices.slice(1, 4).map((price, index) => (
                  <div
                    key={`${price.price_type}-${index}`}
                    className="flex justify-between rounded bg-muted/30 p-2 dark:bg-muted/20"
                  >
                    <div className="text-sm font-medium text-foreground">
                      {price.price_type_display || price.price_type}
                    </div>
                    <div className="font-semibold text-foreground">
                      {formatCurrencyWithOrgFormat(price.amount, price.currency || 'USD')}
                    </div>
                  </div>
                ))}

                {/* View all prices button */}
                {prioritizedPrices && prioritizedPrices.length > 4 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-1 w-full"
                    onClick={handleShowPricing}
                  >
                    <MoreHorizontal className="mr-2 size-4" />
                    {getSidebarLabels.buttons.viewAllPrices}
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Platform Sync Section */}
          <div className="mt-4">
            <ProductSyncButtons productId={product.id} productName={product.name} />
          </div>

          {/* Status and metadata in compact layout */}
          <div className="mt-3 grid grid-cols-2 gap-2 border-t border-border pt-3">
            <div>
              <h2 className="mb-1 text-sm font-medium uppercase tracking-wider text-muted-foreground">
                {getSidebarLabels.sections.status}
              </h2>
              <div className="rounded-md border border-border bg-muted/30 px-3 py-1.5 dark:bg-muted/20">
                <StatusToggle
                  isActive={product.is_active}
                  onToggle={handleStatusToggle}
                  disabled={!hasEditPermission}
                  size="sm"
                  showLabel={true}
                  className="w-full"
                />
              </div>
            </div>

            <div>
              <h2 className="mb-1 text-sm font-medium uppercase tracking-wider text-muted-foreground">
                {getSidebarLabels.sections.created}
              </h2>
              <div className="rounded-md border border-border bg-muted/30 px-3 py-1.5 dark:bg-muted/20">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-muted-foreground">{createdDate.display}</span>
                        <Avatar className="size-4">
                          <AvatarImage
                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(product.created_by || 'User')}&size=32`}
                          />
                          <AvatarFallback className="text-[8px]">
                            {(product.created_by || 'U').charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {getSidebarLabels.tooltips.relativeTimeByUser
                          .replace('{{relativeTime}}', createdDate.relative)
                          .replace(
                            '{{user}}',
                            product.created_by || getSidebarLabels.tooltips.unknownUser
                          )}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </div>

          {/* Last modified in a compact row */}
          <div className="mt-1 flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-1.5 dark:bg-muted/20">
            <span className="text-xs text-muted-foreground">
              {getSidebarLabels.sections.lastModified}
            </span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">{modifiedDate.display}</span>
                    <Avatar className="size-4">
                      <AvatarImage
                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(product.created_by || 'User')}&size=32`}
                      />
                      <AvatarFallback className="text-[8px]">
                        {(product.created_by || 'U').charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {getSidebarLabels.tooltips.relativeTimeByUser
                      .replace('{{relativeTime}}', modifiedDate.relative)
                      .replace(
                        '{{user}}',
                        product.created_by || getSidebarLabels.tooltips.unknownUser
                      )}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </CardContent>

      {/* Pricing Modal */}
      {product?.id && showPricingModal && (
        <PricingModal
          isOpen={showPricingModal}
          onClose={() => setShowPricingModal(false)}
          productId={product.id}
          onPricesUpdated={handlePricesUpdated}
        />
      )}

      {/* Category Modal */}
      <CategoryModal
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        productId={product.id || 0}
        currentCategory={
          categoryTrail.length > 0 ? categoryTrail[categoryTrail.length - 1] : singleCategory
        }
        onCategoryChanged={async category => {
          // Invalidate queries to refresh the product detail sidebar
          await queryClient.invalidateQueries({
            queryKey: ['product', product.id],
          });
        }}
      />

      {/* Product Tags Modal */}
      <ProductTagsModal
        open={showTagsModal}
        onOpenChange={setShowTagsModal}
        productId={product.id || 0}
        currentTags={product.tags || []}
        onTagsUpdated={async () => {
          await queryClient.invalidateQueries({
            queryKey: ['product', product.id],
          });
        }}
      />

      {/* Family Change Modal */}
      <FamilyChangeModal
        isOpen={showFamilyModal}
        onClose={() => setShowFamilyModal(false)}
        product={product}
        onFamilyChanged={handleFamilyChanged}
      />
    </Card>
  );
}

export default ProductDetailSidebar;
